/**
 * Spec 086 — Fan-Out graph page.
 *
 * Consultant-facing (authenticated, no role gate): renders the fan-out
 * summary strip, the lazy-loaded force graph of clusters, a sortable
 * cluster list, a stacked source-confidence bar, and per-engine tiles.
 *
 * Phase 9 Session C gap 086-1.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { useBrand } from '../../lib/brand-context.js';
import { EmptyState } from '../../components/EmptyState.js';
import { LazyForceGraph } from '../../components/LazyForceGraph.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RunFanoutSynthesisButton } from '../../components/RunFanoutSynthesisButton.js';
import { Region, RegionHead } from '../../components/ui/Region.js';
import { StatRow, Stat } from '../../components/ui/StatRow.js';
import { Chip } from '../../components/ui/Chip.js';
import { Select } from '../../components/Select.js';
import { useResolvedTokenColor } from '../../lib/resolve-token-color.js';
import {
  getFanoutGraphSummary,
  listFanoutClusters,
  type FanoutClusterRow,
  type FanoutGraphSummary,
} from '../../api/fanout.js';
import { CHART_ACTION, inkAtStep } from '../../components/chartTheme.js';

type ClusterSortKey = 'query_count_desc' | 'label_asc';

// Spec 234 §5: monochrome — "observed" is the number this bar exists to
// show (how much of the graph is directly verified rather than inferred),
// so it alone takes the action colour. The three inferred tiers step down
// the same ink ramp in confidence order, high -> low, rather than a
// categorical palette that doesn't say which tier is more trustworthy.
const SOURCE_CONFIDENCE_COLORS = {
  observed: CHART_ACTION,
  inferred_high: inkAtStep(0),
  inferred_medium: inkAtStep(1),
  inferred_low: inkAtStep(2),
} as const;

const SOURCE_CONFIDENCE_LABELS = {
  observed: 'Observed',
  inferred_high: 'Inferred (high)',
  inferred_medium: 'Inferred (medium)',
  inferred_low: 'Inferred (low)',
} as const;

function formatPct(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return '0%';
  return `${Math.round(fraction * 100)}%`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  return `${days}d ago`;
}

export function FanOutGraphPage() {
  const { selectedBrand, loading: brandsLoading } = useBrand();
  const brandId = selectedBrand?.id ?? null;

  const [summary, setSummary] = useState<FanoutGraphSummary | null>(null);
  const [clusters, setClusters] = useState<FanoutClusterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<ClusterSortKey>('query_count_desc');

  const load = useCallback(async (): Promise<number> => {
    if (!brandId) return 0;
    setLoading(true);
    setError(null);
    try {
      const [s, cs] = await Promise.all([
        getFanoutGraphSummary(brandId).catch(() => null),
        listFanoutClusters(brandId).catch(() => []),
      ]);
      setSummary(s);
      setClusters(cs);
      // Prefer the authoritative summary count; fall back to the list length.
      return s?.total_clusters ?? cs.length;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return 0;
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedClusters = useMemo(() => {
    const rows = [...clusters];
    if (sortKey === 'query_count_desc') {
      rows.sort((a, b) => b.query_count - a.query_count);
    } else {
      rows.sort((a, b) => a.semantic_label.localeCompare(b.semantic_label));
    }
    return rows;
  }, [clusters, sortKey]);

  // react-force-graph-2d paints node.color straight onto Canvas2D's fillStyle,
  // which knows nothing about CSS custom properties — a var() string here
  // would silently fail to paint. Resolve the accent token to a concrete
  // colour in JS instead (spec 228), and re-resolve on theme change.
  const nodeColor = useResolvedTokenColor('--hub-color-action');

  // Nodes sized by query_count (minimum 1 to stay visible). No edges — the
  // backend does not expose inter-cluster similarity; faking edges would
  // mislead. Uniform accent node color for v1 per spec.
  const graphData = useMemo(
    () => ({
      nodes: sortedClusters.map((c) => ({
        id: c.slug,
        name: c.semantic_label,
        val: Math.max(1, c.query_count),
        color: nodeColor,
      })),
      links: [] as unknown[],
    }),
    [sortedClusters, nodeColor],
  );

  // Phase 9 Session E.2 (gap XC-R2-1): outer spinner while the brand list
  // is still resolving — prevents the "No brand selected" flash that round-2
  // QA flagged.
  if (brandsLoading) {
    return (
      <div
        data-testid="brands-loading"
        className="hub-page--dashboard items-center justify-center py-16 text-slate-500"
      >
        <Spinner className="text-violet-600" />
        <p className="mt-3 text-sm">Loading brands…</p>
      </div>
    );
  }

  if (!selectedBrand) {
    return (
      <div data-testid="fanout-graph-page" className="hub-page--dashboard" data-density="compact">
        <Region>
          <Breadcrumbs items={[{ label: 'Fan-out' }]} />
          <div className="hub-page__head mt-3">
            <h1 className="hub-page__title">Fan-out</h1>
          </div>
        </Region>
        <Region>
          <EmptyState
            title="No brand selected"
            description="Select a brand to view its fan-out clusters."
          />
        </Region>
      </div>
    );
  }

  return (
    /* Spec 236 §2 — the DASHBOARD archetype, and the argument matters because
       `hub-page--canvas` is the tempting answer for a page that draws a force
       graph. It is the wrong one. Kridt's dashboard recipe reads: a region for
       the title and actions, a stat row of cells that touch, an unequal split
       divided by a line, a table region. That is this page part for part — a
       normally scrolling stack that happens to hold a fixed 620x400 graph
       inside the split. Canvas means 100dvh, a docked inspector and a page
       that never scrolls, which is a structural rewrite, and gate 123.A.2
       independently forbids the full-bleed hook on this page — which blocks
       the full bleed a canvas needs. (That gate counts the hook's NAME in this
       file and requires zero, so it must not appear here even in a comment.)
       `hub-page--canvas` therefore stays unused: nothing in this app is
       canvas-shaped. */
    <div data-testid="fanout-graph-page" className="hub-page--dashboard" data-density="compact">
      <Region>
        <Breadcrumbs items={[{ label: 'Fan-out' }]} />
        <div className="hub-page__head mt-3">
          <div>
            <h1 className="hub-page__title">Fan-out</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
              Query clusters synthesized for {selectedBrand.name}.
            </p>
          </div>
          {/* Recompute affordance — only when clusters already exist; the
              empty state below carries the first-run trigger. */}
          {clusters.length > 0 && (
            <RunFanoutSynthesisButton
              brandId={selectedBrand.id}
              onComplete={load}
              recompute
            />
          )}
        </div>
      </Region>

      {error && (
        <Region>
          <div className="flex flex-wrap items-center gap-2 text-sm text-rose-800">
            <Chip tone="fail">Error</Chip>
            {error}
          </div>
        </Region>
      )}

      {loading && !summary && (
        <Region>
          <div className="flex items-center gap-2 text-sm text-[var(--hub-color-ink-muted)]">
            <Spinner  />
            Loading fan-out data…
          </div>
        </Region>
      )}

      {/* Summary strip. Was a four-up grid of tiles inside a bordered card;
          the card chrome was decoration around what is really one instrument,
          so it becomes a `.hub-stats` row whose cells touch and are divided by
          hairlines. No cell takes `attention` — nothing here is a figure that
          asks for action, and gate 231.A.3 allows at most one per row anyway. */}
      {summary && (
        <Region>
          <StatRow data-testid="fanout-summary">
            <Stat label="Queries" value={summary.total_queries.toLocaleString()} />
            <Stat label="Clusters" value={summary.total_clusters.toLocaleString()} />
            <Stat
              label="Observed"
              note="directly verified, not inferred"
              value={formatPct(summary.observed_ratio)}
            />
            <Stat
              label="Last recomputed"
              value={formatRelative(summary.last_recomputed_at)}
            />
          </StatRow>
        </Region>
      )}

      {/* Source-confidence stacked bar */}
      {summary && summary.total_queries > 0 && (
        <Region>
          <RegionHead title="Source confidence" />
          <SourceConfidenceBar counts={summary.queries_by_confidence} />
          <div className="mt-3 flex flex-wrap gap-3 text-xs">
            {(
              Object.keys(SOURCE_CONFIDENCE_COLORS) as Array<
                keyof typeof SOURCE_CONFIDENCE_COLORS
              >
            ).map((key) => (
              <span key={key} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-3"
                  style={{ backgroundColor: SOURCE_CONFIDENCE_COLORS[key] }}
                />
                <span className="text-[var(--hub-color-ink-muted)]">
                  {SOURCE_CONFIDENCE_LABELS[key]}
                </span>
                <span className="font-mono text-[var(--hub-color-ink-subtle)]">
                  ({summary.queries_by_confidence[key]})
                </span>
              </span>
            ))}
          </div>
        </Region>
      )}

      {/* Engine coverage — one cell per engine, so this is a stat row too
          rather than a tray of bordered tiles. `StatRow` derives
          `--hub-stats-count` from the child count, which is what keeps the
          five engines from needing a Tailwind grid class that would fight
          the archetype. */}
      {summary && Object.keys(summary.queries_by_engine).length > 0 && (
        <Region>
          <RegionHead title="Engine coverage" />
          <StatRow data-testid="fanout-engine-coverage">
            {Object.entries(summary.queries_by_engine).map(([engine, count]) => (
              <Stat key={engine} label={engine} value={count.toLocaleString()} />
            ))}
          </StatRow>
        </Region>
      )}

      {/* SPEC 236 §3 — THIS SPLIT STAYS, AND GAINS ITS LINE.
          `lg:grid-cols-[2fr_1fr]` is not an accident of an earlier layout: it
          IS Kridt's "unequal split divided by a line", the graph reading wider
          than the list that indexes it. The conversion is therefore to delete
          the gap and draw the hairline, not to flatten the columns. */}
      <section className="grid grid-cols-1 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0 p-5">
          <header className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-[var(--hub-color-ink)]">
              Cluster map
            </h2>
            <span className="text-xs text-[var(--hub-color-ink-muted)]">
              {clusters.length} cluster{clusters.length === 1 ? '' : 's'} · sized
              by query count
            </span>
          </header>
          {clusters.length === 0 ? (
            <div
              data-testid="fanout-graph-empty"
              className="flex flex-col items-center gap-4 py-8 text-center"
            >
              <EmptyState
                title="No query clusters yet"
                description={
                  summary && summary.total_queries === 0
                    ? 'This brand needs collected responses first — run a collection, then synthesize.'
                    : "Run fan-out synthesis to cluster this brand's collected queries."
                }
                variant="compact"
              />
              <RunFanoutSynthesisButton
                brandId={selectedBrand.id}
                onComplete={load}
              />
            </div>
          ) : (
            <div data-testid="fanout-force-graph">
              <LazyForceGraph
                graphData={graphData}
                width={620}
                height={400}
                nodeLabel="name"
              />
            </div>
          )}
        </div>

        {/* The line. On wide viewports it divides the two columns; when the
            grid collapses to one column it rotates to the top, the same way
            `.hub-page__split` behaves in layout.css. */}
        <div className="min-w-0 border-t border-[var(--hub-color-line)] p-5 lg:border-l lg:border-t-0">
          <header className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-[var(--hub-color-ink)]">Clusters</h2>
            <Select
              options={[
                { value: 'query_count_desc', label: 'Most queries' },
                { value: 'label_asc', label: 'A → Z' },
              ]}
              value={sortKey}
              onChange={(v) => setSortKey(v as ClusterSortKey)}
              className="w-40"
              data-testid="fanout-clusters-sort"
            />
          </header>
          {clusters.length === 0 ? (
            <p className="py-4 text-center text-sm text-[var(--hub-color-ink-muted)]">
              No clusters yet.
            </p>
          ) : (
            <ul
              data-testid="fanout-clusters-list"
              className="max-h-[420px] divide-y divide-[var(--hub-color-line)] overflow-y-auto"
            >
              {sortedClusters.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/fanout/clusters/${encodeURIComponent(c.slug)}`}
                    data-testid={`fanout-cluster-link-${c.slug}`}
                    className="flex items-start justify-between gap-3 px-2 py-2 hover:bg-[var(--hub-color-surface-sunken)]"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {c.semantic_label}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        {c.primary_sub_query_type}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800">
                      {c.query_count}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

/* `SummaryTile` is gone rather than restyled: the four tiles it drew are now
   `Stat` cells inside a `StatRow`, which is the same information expressed as
   one instrument instead of four. */

function SourceConfidenceBar({
  counts,
}: {
  counts: FanoutGraphSummary['queries_by_confidence'];
}) {
  const total =
    counts.observed +
    counts.inferred_high +
    counts.inferred_medium +
    counts.inferred_low;
  if (total === 0) {
    return (
      <div className="h-3 rounded-full bg-slate-100" aria-label="No data" />
    );
  }
  const segments: Array<{
    key: keyof typeof SOURCE_CONFIDENCE_COLORS;
    count: number;
  }> = [
    { key: 'observed', count: counts.observed },
    { key: 'inferred_high', count: counts.inferred_high },
    { key: 'inferred_medium', count: counts.inferred_medium },
    { key: 'inferred_low', count: counts.inferred_low },
  ];
  return (
    <div
      data-testid="fanout-confidence-bar"
      className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100"
      role="img"
      aria-label="Source-confidence distribution"
    >
      {segments.map(({ key, count }) =>
        count > 0 ? (
          <span
            key={key}
            style={{ width: `${(count / total) * 100}%`, backgroundColor: SOURCE_CONFIDENCE_COLORS[key] }}
            title={`${SOURCE_CONFIDENCE_LABELS[key]}: ${count} (${Math.round((count / total) * 100)}%)`}
          />
        ) : null,
      )}
    </div>
  );
}
